import { ReactNode } from "react";

import styled from "styled-components";

import useProperty from "hooks/useProperty";

import { WellDetailCardData } from "api/data";

import { EntityKind } from "models/entityKind";

import { useIsFieldGroupEnabled } from "./PanelField";
import { INK, MISSING, MISSING_VALUE } from "./styles";

export interface PanelHeaderFieldProps {
  /** The header labels are fixed for v1, so they are not read from the catalogue. */
  label: string;
  propertyName?: string;
  wellData?: Maybe<WellDetailCardData>;
  value?: Maybe<string>;
  /** Rendered beside the value. */
  children?: ReactNode;
}

/**
 * Label-over-value pair in the identity block. Status is plain ink text — no
 * dot and no colour.
 */
export function PanelHeaderField({
  label,
  propertyName,
  wellData,
  value,
  children
}: PanelHeaderFieldProps): JSX.Element {
  const property = useProperty(propertyName ?? "", false, "", EntityKind.Well);
  const groupEnabled = useIsFieldGroupEnabled(propertyName ? property?.group : null);

  if (!groupEnabled) return null;

  const raw = propertyName ? wellData?.[propertyName] : value;
  const resolved = typeof raw === "string" && raw !== "" ? raw : null;

  return (
    <Field>
      <Label>{label}</Label>
      <Value $missing={!resolved}>
        {resolved ?? MISSING_VALUE}
        {children}
      </Value>
    </Field>
  );
}

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const Label = styled.span`
  font-size: 12px;
  color: #3c4858;
`;

const Value = styled.span<{ $missing: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 15px;
  font-weight: 700;
  color: ${(props) => (props.$missing ? MISSING : INK)};
`;
